import React from 'react';
import { Link } from 'react-router-dom';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';
import { 
  CheckCircle2, 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  BookOpen, 
  Barcode, 
  Building2, 
  FileCheck, 
  Globe2, 
  ExternalLink 
} from 'lucide-react';

export const ProcessPage: React.FC = () => {
  return (
    <div className="space-y-16 py-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-sans antialiased text-slate-900">
      
      {/* 1. Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-[11px] font-bold uppercase tracking-wider font-mono">
          <Sparkles className="w-3.5 h-3.5 text-brand-600" />
          <span>Statutory &amp; Editorial Milestones</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-black text-slate-900 tracking-tight">
          Publishing Lifecycle &amp; Governance
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium">
          From initial theological appraisal to statutory Government of India ISBN allotment and national legal deposit, <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> executes every project with uncompromising rigor.
        </p>
      </section>

      {/* 2. DEDICATED TECHNICAL SECTION: GOVERNMENT OF INDIA ISBN REGISTRATION (isbn.gov.in) */}
      <section className="p-6 sm:p-8 rounded-3xl bg-slate-950 text-white border border-slate-800 shadow-xl space-y-8">
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-400 font-bold">
                Official Statutory Protocol &bull; Government of India
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-100">
              Technical ISBN Allotment via Raja Rammohun Roy National Agency (<a href="https://isbn.gov.in/" target="_blank" rel="noopener noreferrer" className="text-brand-300 hover:underline">isbn.gov.in</a>)
            </h2>
          </div>

          <a
            href="https://isbn.gov.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-850 text-brand-300 text-xs font-bold border border-slate-700 transition-all inline-flex items-center gap-1.5 shrink-0 self-start md:self-auto"
          >
            <span>isbn.gov.in Portal</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
          
          <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
            <div className="flex items-center gap-2 text-brand-400 font-mono font-bold">
              <Building2 className="w-4 h-4" />
              <span>Step 1: Corporate KYC &amp; Publisher Registry</span>
            </div>
            <h4 className="text-sm font-bold text-white">Legal Entity Verification</h4>
            <p className="text-slate-300 leading-relaxed text-[11px]">
              Filing formal corporate credentials of <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> with the Ministry of Education (Department of Higher Education). Includes submission of Corporate CIN, Permanent Account Number (PAN), Registered Office Address Proof, and Board Resolution for Authorized Signatory.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
            <div className="flex items-center gap-2 text-brand-400 font-mono font-bold">
              <FileCheck className="w-4 h-4" />
              <span>Step 2: Bibliographic Classification &amp; Metadata Filing</span>
            </div>
            <h4 className="text-sm font-bold text-white">DDC &amp; BISAC Indexing</h4>
            <p className="text-slate-300 leading-relaxed text-[11px]">
              Exact catalog entry on <strong className="text-white">isbn.gov.in</strong> documenting Title, Subtitle, Author/Translator legal names, language classification, binding format (Paperback / Clothbound Hardcover / EPUB), Dewey Decimal Classification (DDC), and BISAC theological subject categorization.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
            <div className="flex items-center gap-2 text-brand-400 font-mono font-bold">
              <BookOpen className="w-4 h-4" />
              <span>Step 3: Pre-Publication Camera-Ready Proofs</span>
            </div>
            <h4 className="text-sm font-bold text-white">Statutory Imprint Verification</h4>
            <p className="text-slate-300 leading-relaxed text-[11px]">
              Upload of verified pre-press proofs: Half-Title page, Full Title page, Verso / Imprint copyright page (specifying copyright holder, translation licenses, and publisher legal imprint), Table of Contents, and formal declaration of non-infringement under the Indian Copyright Act, 1957.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
            <div className="flex items-center gap-2 text-brand-400 font-mono font-bold">
              <Barcode className="w-4 h-4" />
              <span>Step 4: 13-Digit Allotment &amp; Vector Barcode Prep</span>
            </div>
            <h4 className="text-sm font-bold text-white">EAN-13 Pre-Press Integration</h4>
            <p className="text-slate-300 leading-relaxed text-[11px]">
              Administrative allocation of the official 13-digit ISBN (Prefix: 978-93 or 978-81). Our production team generates ISO/IEC 15420-compliant vector barcodes with strict Quiet Zones and Human Readable Interpretation (HRI) font placement on the back cover.
            </p>
          </div>

        </div>

        {/* Legal Deposit Box */}
        <div className="p-5 rounded-2xl bg-slate-900 border border-brand-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-brand-300 font-mono font-bold text-xs">
              <ShieldCheck className="w-4 h-4 text-brand-400" />
              <span>Statutory Compliance: Delivery of Books Act, 1954</span>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
              Within 30 days of release, physical statutory copies of every title are delivered to the <strong>National Library of India (Kolkata)</strong> and designated repository libraries in New Delhi, Mumbai, and Chennai for permanent preservation in the Indian National Bibliography.
            </p>
          </div>
          <span className="shrink-0 px-3 py-1.5 rounded-lg bg-brand-950 text-brand-300 font-mono text-[10px] font-bold border border-brand-700/50">
            Mandatory Statutory Filing
          </span>
        </div>

      </section>

      {/* 3. 12-Stage Timeline Steps */}
      <section className="space-y-4">
        <div className="text-center space-y-1 pb-4">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Editorial Progression
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            The 12-Stage Editorial Pathway
          </h2>
        </div>

        <div className="space-y-6 relative before:absolute before:inset-y-0 before:left-6 md:before:left-1/2 before:w-0.5 before:bg-slate-200">
          {PUBLISHING_STAGES_CONFIG.map((stage, idx) => (
            <div
              key={stage.status}
              className={`relative flex flex-col md:flex-row gap-6 items-start ${
                idx % 2 === 0 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Center Marker */}
              <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white border-4 border-brand-500 flex items-center justify-center font-mono font-bold text-xs text-brand-900 shadow-md z-10">
                {stage.order}
              </div>

              {/* Content Card */}
              <div className="ml-16 md:ml-0 md:w-[calc(50%-2.5rem)] p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 transition-all space-y-2">
                <div className="flex items-center justify-between text-xs font-mono text-brand-700 font-bold uppercase">
                  <span>Phase {Math.ceil(stage.order / 3)}</span>
                  <span>Stage {stage.order} of 12</span>
                </div>
                <h3 className="font-serif text-base font-bold text-slate-900">
                  {stage.label}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-sans">
                  {stage.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Author / Rights Inquiries CTA */}
      <section className="p-8 sm:p-12 rounded-3xl bg-slate-950 text-white text-center space-y-6 border border-slate-800">
        <div className="max-w-2xl mx-auto space-y-3">
          <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
            Publishing Partnerships &amp; Acquisitions
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
            Submit a Proposal to FOR THE TRUTH PRESS
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 font-light max-w-lg mx-auto leading-relaxed">
            We partner with authors, rights holders, and academic seminaries to bring theological conviction and vernacular literature to press.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link
            to="/submit-enquiry"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-md transition-all"
          >
            <span>Begin Publishing Enquiry</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/publications"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 font-semibold text-xs transition-all"
          >
            <span>Explore 432 Production Records</span>
          </Link>
        </div>
      </section>

    </div>
  );
};
